/**
 * WebsiteContactForm — POST /api/website-leads
 * On success, writes to the Lead Inbox and triggers owner-email + (future) SMS.
 *
 * Honeypot: hidden `company` field — bots fill it, humans don't.
 */
import { useState } from 'react';
import { Loader2, CheckCircle2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function WebsiteContactForm({ config }) {
  const form = config.estimate_form || {};
  const projectTypes = Array.isArray(form.project_types) ? form.project_types : [];
  const primary = config.brand?.primary || '#0f4c81';
  const accent = config.brand?.accent || primary;

  const [state, setState] = useState({
    customer_name: '',
    phone: '',
    email: '',
    service_address: '',
    project_type: projectTypes[0] || '',
    project_description: '',
    preferred_time: '',
    company: '', // honeypot
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const update = (k) => (e) => setState((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!state.customer_name.trim() || (!state.phone.trim() && !state.email.trim())) {
      setErrorMsg('Please share your name and a phone or email so we can reply.');
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const resp = await fetch(`${API}/api/website-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: config.slug,
          industry: config.industry,
          source_domain: typeof window !== 'undefined' ? window.location.hostname : '',
          ...state,
        }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || `Submission failed (${resp.status})`);
      }
      setStatus('success');
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong — please try again or call us.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <section id="estimate" className="py-20 lg:py-24 bg-slate-50" data-testid="ws-form-section">
        <div className="max-w-xl mx-auto px-5 lg:px-8 text-center">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: `${primary}1a`, color: primary }}
          >
            <CheckCircle2 size={28} />
          </div>
          <h2 className="text-2xl lg:text-3xl font-black tracking-tight text-slate-900">
            Got it — we’re on it.
          </h2>
          <p className="mt-3 text-base text-slate-600 leading-relaxed">
            {form.success_message ||
              'Thanks — your request is in. We’ll reach out shortly to schedule your free estimate.'}
          </p>
          {config.phone_display && (
            <p className="mt-5 text-sm text-slate-500">
              Need it sooner? Call{' '}
              <a
                href={`tel:${(config.phone_number || '').replace(/\s/g, '')}`}
                className="font-bold underline"
                style={{ color: primary }}
              >
                {config.phone_display}
              </a>
            </p>
          )}
        </div>
      </section>
    );
  }

  return (
    <section id="estimate" className="py-20 lg:py-24 bg-slate-50" data-testid="ws-form-section">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-10">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2.5">
            Get an estimate
          </p>
          <h2 className="text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
            {form.title || 'Request a Free Estimate'}
          </h2>
          {form.subtitle && (
            <p className="mt-3 text-base text-slate-600">{form.subtitle}</p>
          )}
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 lg:p-8 space-y-4"
          data-testid="ws-estimate-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Your name *">
              <input
                type="text"
                required
                value={state.customer_name}
                onChange={update('customer_name')}
                className="ws-input"
                data-testid="ws-form-name"
              />
            </Field>
            <Field label="Phone">
              <input
                type="tel"
                value={state.phone}
                onChange={update('phone')}
                placeholder="(831) 555-0100"
                className="ws-input"
                data-testid="ws-form-phone"
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={state.email}
                onChange={update('email')}
                placeholder="you@example.com"
                className="ws-input"
                data-testid="ws-form-email"
              />
            </Field>
            <Field label="Project address">
              <input
                type="text"
                value={state.service_address}
                onChange={update('service_address')}
                placeholder="Street, City"
                className="ws-input"
                data-testid="ws-form-address"
              />
            </Field>
            <Field label="Project type">
              <select
                value={state.project_type}
                onChange={update('project_type')}
                className="ws-input bg-white"
                data-testid="ws-form-project-type"
              >
                {projectTypes.length === 0 && <option value="">Select…</option>}
                {projectTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Best time to reach you">
              <input
                type="text"
                value={state.preferred_time}
                onChange={update('preferred_time')}
                placeholder="Morning, evenings, etc."
                className="ws-input"
                data-testid="ws-form-time"
              />
            </Field>
          </div>

          <Field label="Tell us about the project">
            <textarea
              rows={4}
              value={state.project_description}
              onChange={update('project_description')}
              placeholder="What needs to be done? Rough size or # of rooms is helpful."
              className="ws-input resize-y"
              data-testid="ws-form-description"
            />
          </Field>

          {/* Honeypot — hidden from users, traps bots */}
          <div aria-hidden className="hidden">
            <label>
              Company
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={state.company}
                onChange={update('company')}
              />
            </label>
          </div>

          {status === 'error' && errorMsg && (
            <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="ws-form-error">
              {errorMsg}
            </p>
          )}

          <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="inline-flex items-center justify-center gap-2 text-white font-bold px-7 py-3.5 rounded-full text-sm shadow-md transition-all disabled:opacity-60 hover:scale-[1.02] active:scale-[0.99] w-full sm:w-auto"
              style={{ backgroundColor: accent }}
              data-testid="ws-form-submit"
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send my estimate request <ArrowRight size={16} />
                </>
              )}
            </button>
            <span className="text-xs text-slate-500">
              No obligation · usually back to you the same day
            </span>
          </div>
        </form>

        <style>{`.ws-input{width:100%;height:44px;padding:0 14px;border-radius:10px;border:1px solid #e2e8f0;background:#fff;font-size:14px;color:#0f172a;outline:none;transition:border-color .15s, box-shadow .15s}.ws-input:focus{border-color:${primary};box-shadow:0 0 0 3px ${primary}26}.ws-input.resize-y{height:auto;padding:12px 14px;line-height:1.5}`}</style>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
